import { useLocation, useNavigate } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';

type NavigateQueryOptions = NavigateOptions & {
  types?: string | string[];
  transition?: Direction;
  remove?: boolean;
};

enum Direction {
  backward = -1, //from right to left
  forward = 1, //from left to right
  none = 0 //without transition
}

export default (
  commonOptions: NavigateOptions = {
    preventScrollReset: false,
    viewTransition: false,
    relative: 'path',
    replace: false
  }
) => {
  let { search } = useLocation();
  const navigate = useNavigate();

  const navigation = (
    route: string | number,
    {
      types,
      ...options
    }: NavigateQueryOptions & Required<Pick<NavigateQueryOptions, 'transition'>>
  ) =>
    (matches && update(route, options)) ||
    document.startViewTransition({
      update: () => update(route, options),
      types: types
        ? (Array.isArray(types) && types) || [types]
        : [(options.transition === 1 && 'right') || 'left']
    });

  const { matches } = matchMedia('(prefers-reduced-motion: reduce)');

  const update = (to: string | number, options?: NavigateQueryOptions) =>
    typeof to === 'string'
      ? navigate(to, { ...commonOptions, ...options })
      : navigate(to);

  return (route: string | number, options?: NavigateQueryOptions) => {
    if (typeof route === 'number')
      return navigation(route, {
        transition: Math.sign(route),
        ...options
      });

    const queryIndex = route.indexOf('?');

    if (search.includes('?') && queryIndex > -1 && !options?.remove) {
      search += `&${route.slice(queryIndex + 1)}`;
      route = route.slice(0, queryIndex);
    }

    const to = `${route}${(!options?.remove && search) || ''}`;

    // When transition direction is not defined, perform a best guess:
    // use slide left for the home page (/) and the right one for others.
    const transition = options?.transition ?? ((to.length === 1 && -1) || 1);

    return (
      (!transition && update(to, options)) ||
      navigation(to, { transition, ...options })
    );
  };
};
